import { CommonModule, formatDate } from '@angular/common';
import {
  Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { AttachmentViewerComponent } from '@app/lib/components/attachment-viewer/attachment-viewer.component';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { IBreadcrumb } from '@app/lib/models/breadcrumb.model';
import { IAttachmentDialogData } from '@app/lib/models/dialog-data.model';
import { IResponse } from '@app/lib/models/response.model';
import { debounceTime, distinctUntilChanged, filter, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { ButtonComponent } from '../../lib/components/button/button.component';
import {
  ICaseItem, ICaseSearchRequestBody, ICourtUnitType, ISearchRequestParams
} from '../models/case-assignment.model';
import { ICaseStatus, ICaseType } from '../models/case-status.model';
import { ICasePhase, IParty } from '../models/party.model';
import { CourtStatsService } from '../services/court-stats.service';
import { ILoginData } from '@app/lib/models/login.model';
import { ECourtId } from '../models/constant';
import { ActivatedRoute } from '@angular/router';
import { REPORT_FORMS } from '@app/config/report-field-config';
import { ISelectOption } from '@app/lib/models/task-stats.model';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { RoleStaff } from '../models/reports.model';
import { CaseNumberSearchComponent } from '../widget/case-number-search/case-number-search.component';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatTableModule,
    MatDatepickerModule,
    BreadcrumbComponent,
    MatProgressSpinner,
    CaseNumberSearchComponent
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
})
export class ReportsComponent implements OnInit {
  // flags + breadcrumbs
  isSupremeCourtFlg = signal(false);
  currentReportType = '';

  breadcrumbs = computed<IBreadcrumb[]>(() => [
    { label: 'Reports' },
    { label: this.formatReportType(this.currentReportType) || 'Report' },
  ]);

  // dynamic form
  formGrp!: FormGroup;
  fields: any[] = [];
  searchTextMap: Record<string, string> = {};
  filteredOptionsMap: Record<string, any[]> = {};

  // data signals
  public partyNames: WritableSignal<IParty[]> = signal([]);
  public caseStatusList: WritableSignal<ICaseStatus[]> = signal([]);
  public divisionList: WritableSignal<ICourtUnitType[]> = signal([]);
  public caseTypeList: WritableSignal<ICaseType[]> = signal([]);
  public courtList: WritableSignal<ICourtUnitType[]> = signal([]);
  public casePhases: WritableSignal<ICasePhase[]> = signal([]);

  // search result buckets per field name
  searchResultsMap: { [key: string]: IParty[] | ICaseItem[] } = {};

  loadingMap: Record<string, boolean> = {};
  selectOptionsMap: Record<string, any[]> = {};

  // misc
  today = new Date();
  courtIdData: number | null = null;
  isDivision: string | null = null;
  userType: string = '';
  isDivisionId: number | null = null;
  isdivisionreadOnly = false;

  private _sub: DestroyRef = inject(DestroyRef);

  constructor(
    private dialog: MatDialog,
    private service: CourtStatsService,
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) { }

  // --------------------- lifecycle ---------------------
  ngOnInit(): void {
    // 1) read route, build form, THEN wire listeners
    this.route.url.subscribe((segments) => {
      const path = segments.map((s) => s.path).join('/');
      this.currentReportType = path.split('/').pop() || '';
      this.initializeForm();
      this.wireDynamicSearches();   // <-- wire valueChanges for all search fields
      this.preloadSelectOptions();
      this.getCourtNameData();

      // Add this specific call for statistics reports
      if (this.currentReportType === 'case-statistics' || this.currentReportType === 'appeal-statistics') {
        this.setupStatisticsValidation();
      }
      this.formGrp.reset();
      this.formGrp.valueChanges.pipe(takeUntilDestroyed(this._sub)).subscribe(() => {
        this.fields = [...this.fields]; // triggers re-render
      });
    });
  }

  private setupStatisticsValidation(): void {
    const filedOn = this.formGrp.get('filedOn');
    const fromDate = this.formGrp.get('fromDate');
    const tillDate = this.formGrp.get('tillDate');

    if (!filedOn || !fromDate || !tillDate) return;

    // Listen to Filed On changes
    filedOn.valueChanges.pipe(takeUntilDestroyed(this._sub)).subscribe((val) => {
      if (val) {
        // 1. If Filed On has value, disable range fields
        fromDate.disable({ emitEvent: false });
        tillDate.disable({ emitEvent: false });
        fromDate.clearValidators();
        tillDate.clearValidators();
        filedOn.setValidators([Validators.required]);
      } else if (!fromDate.value && !tillDate.value) {
        // 2. If Filed On cleared and range is empty, enable everything
        fromDate.enable({ emitEvent: false });
        tillDate.enable({ emitEvent: false });
      }
      this.updateValidity([filedOn, fromDate, tillDate]);
    });

    // Listen to Date Range changes
    const rangeChanges = [fromDate.valueChanges, tillDate.valueChanges];
    rangeChanges.forEach(obs => {
      obs.pipe(takeUntilDestroyed(this._sub)).subscribe(() => {
        const hasRangeValue = fromDate.value || tillDate.value;

        if (hasRangeValue) {
          // 1. If Range has value, disable Filed On
          filedOn.disable({ emitEvent: false });
          filedOn.clearValidators();
          // 2. Make BOTH range fields mandatory
          fromDate.setValidators([Validators.required]);
          tillDate.setValidators([Validators.required]);
        } else if (!filedOn.value) {
          // 3. If range cleared and Filed On is empty, enable everything
          filedOn.enable({ emitEvent: false });
          fromDate.clearValidators();
          tillDate.clearValidators();
        }
        this.updateValidity([filedOn, fromDate, tillDate]);
      });
    });
  }

  public onCaseSearchOptionSelected(item: any): void {
    const caseNo = item?.caseNumber || item?.courtCaseNumber || (typeof item === 'string' ? item : '');
    if (caseNo && this.formGrp.get('caseNumber')) {
      this.formGrp.patchValue({ caseNumber: caseNo });
    }
  }

  private updateValidity(controls: (FormControl | any)[]): void {
    controls.forEach(control => control.updateValueAndValidity({ emitEvent: false }));
  }

  // Helper to determine if the asterisk (*) should show in the UI
  isMandatory(fieldName: string): boolean {
    const control = this.formGrp.get(fieldName);
    if (!control || !control.validator) return false;

    const validator = control.validator({} as any);
    return validator && validator['required'];
  }
  // --------------------- utilities ---------------------
  public formatReportType(type?: string): string {
    if (!type) return '';
    return type
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  // Centralized fetcher based on field label
  fetchSearchResults(label: string, value: string) {
    console.log(label);
    switch (label) {
      case 'Party Name':
        return this.service.getPartyNames({ partiesName: value, partiesId: '' }).pipe(
          tap(res => console.log('Party Name API Response:', res)),
          map(res => res.data || [])
        );

      case 'Case/Appeal Number': {
        const body: ICaseSearchRequestBody = {
          caseNumber: value,
          dateOfFiling: 'null'
        };
        const params: ISearchRequestParams = {
          page: 1, start: 0, limit: 25, property: 'id', direction: 'ASC'
        };
        return this.service.searchCaseNumber(body, params).pipe(
          tap(res => console.log('Case Number API Response:', res)),
          map(res => res?.data ?? [])
        );
      }

      default:
        return of([]);
    }
  }

  // How the selected item appears in the input
  displaySearchResult(fieldName: string, value: any): string {
    if (!value) return '';

    // If user is still typing
    if (typeof value === 'string') return value;

    // If user already selected an object
    switch (fieldName) {
      case 'Party Name':
        return value.partiesFullName || value.partiesName || '';
      case 'Case/Appeal Number':
      case 'Case Number':
        return value.caseNumber || '';
      default:
        // fallback for any other search fields
        return value.name || value.label || '';
    }
  }


  // How each option is rendered in the dropdown
  getDisplayLabel(item: IParty | ICaseItem): string {
    if ('prtType' in item && 'partiesFullName' in item) {
      return `${item.prtType} - ${item.partiesFullName}`;
    }
    if ('caseCatagory' in item && 'caseNumber' in item) {
      return `${item.caseNumber}- ${item.caseCatagory}-${item.division}`;
    }
    // Fallbacks
    return (item as any).partiesName || (item as any).caseNumber || '';
  }

  // --------------------- form init ---------------------
  initializeForm(): void {
    const config = REPORT_FORMS[this.currentReportType] || [];
    this.fields = config;

    const controls: Record<string, any> = {};
    for (const f of this.fields) {
      controls[f.name] = f.required ? ['', Validators.required] : [''];
    }
    this.formGrp = this.fb.group(controls);

    if (this.formGrp.get('staffName')) {
      this.formGrp.get('staffName')?.disable();
    }
  }

  getLabel(label: string): string {
    // If it's not the Supreme Court or the label is empty, return it as-is
    if (!this.isSupremeCourtFlg || !label) {
      return label;
    }

    // Handle replacements sequentially (plurals first)
    return label
      .replace(/Cases/g, 'Appeals')
      .replace(/cases/g, 'appeals')
      .replace(/Case/g, 'Appeal')
      .replace(/case/g, 'appeal');
  }
  // Wire valueChanges for every 'search' control defined by fields
  private wireDynamicSearches(): void {
    this.fields
      .filter(f => f.type === 'search')
      .forEach(field => {
        const control = this.formGrp.get(field.name);
        if (!control) return;

        control.valueChanges
          .pipe(
            takeUntilDestroyed(this._sub),
            filter((val: any) => typeof val === 'string' && val.trim().length > 2),
            debounceTime(400),
            distinctUntilChanged(),
            switchMap((val: string) => this.fetchSearchResults(field.label, val))
          )
          .subscribe((res) => {
            this.searchResultsMap[field.name] = res;
          });
      });
  }

  // --------------------- helpers ---------------------
  getResults(name: string): (IParty | ICaseItem)[] {
    return (this.searchResultsMap[name] as (IParty | ICaseItem)[]) || [];
  }

  getCourtNameData(): ILoginData | null {
    try {
      const userDataStr = localStorage.getItem('userData');
      const userData = userDataStr ? (JSON.parse(userDataStr) as ILoginData) : null;
      if (!userData) return null;

      this.courtIdData = userData.courtId;
      this.userType = this.courtIdData === 1 || this.courtIdData === 2 ? 'admin' : 'lawyer';
      this.isDivision = userData.divisionName;
      this.isDivisionId = userData.divisionId;
      this.isSupremeCourtFlg.set(this.courtIdData === ECourtId.SC_ID);

      // check if division and court fields exist
      const hasDivisionField = this.fields.some(f => f.name === 'courtDivision' || f.name === 'division');
      const hasCourtField = this.fields.some(f => f.name === 'courtCombo' || f.name === 'court' || f.name === 'courts');
      const hasCounselField = this.fields.some(f => f.name === 'counselName');

      if (hasDivisionField) {
        this.loadingMap['courtDivision'] = true;

        this.service.getDivisions()
          .pipe(finalize(() => (this.loadingMap['courtDivision'] = false)))
          .subscribe({
            next: (res) => {
              const list = (res.data || res.success || []).map((d: any) => ({
                label: d.courtUnitTypeName,
                value: d.courtUnitTypeId
              }));

              // save to selectOptionsMap
              this.selectOptionsMap['courtDivision'] = list;
              this.filteredOptionsMap['courtDivision'] = [...list];

              // find and set initial division if available
              let divisionToSelect: any = null;
              if (this.isDivisionId && this.isDivisionId !== 0) {
                divisionToSelect = list.find((d: any) => d.value === this.isDivisionId);
                if (divisionToSelect) {
                  this.isdivisionreadOnly = true;
                }
              }

              if (!divisionToSelect && list.length === 1) {
                divisionToSelect = list[0];
                this.isdivisionreadOnly = false;
              }

              if (divisionToSelect) {
                this.formGrp.patchValue({ courtDivision: divisionToSelect });
                this.onSelectChange('courtDivision', { value: divisionToSelect } as MatSelectChange);
              } else {
                this.isdivisionreadOnly = false;
              }


            },
            error: (err) => {
              console.error('Division list fetch error:', err);
              this.selectOptionsMap['courtDivision'] = [];
            }
          });
      }

      return userData;
    } catch (err) {
      console.error('Error in getCourtNameData:', err);
      return null;
    }
  }


  divisionChange(e: MatSelectChange): void {
    const val = e.value as number;
    this.service.getCourtsByDivision({ courtUnitTypeId: val || '' }).subscribe({
      next: (res) => this.courtList.set(res.data || res.success || []),
    });
  }

  onResetForm(): void {
    this.formGrp.reset();
    this.getCourtNameData();
  }

  formSubmitHandler(): void {
    if (!this.formGrp.valid) return;

    const formData = this.formGrp.value;
    console.log('test', formData);

    const queryParams = this.buildQueryParams(this.currentReportType);

    this.openAttachmentDialog(queryParams);
  }

  private openAttachmentDialog(queryParams: Record<string, any>): void {
    const data: IAttachmentDialogData = {
      path: this.getReportEndpoint(this.currentReportType),
      queryParams,
    };

    const dialogRef = this.dialog.open(AttachmentViewerComponent, {
      panelClass: 'dialog-pdf-viewer',
      data,
      disableClose: true,
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this._sub))
      .subscribe(() => this.onResetForm());
  }

  get sortedCourtList(): ICourtUnitType[] {
    return this.courtList()
      .slice()
      .sort((a, b) => {
        const nameA = a.courtUnitTypeName;
        const nameB = b.courtUnitTypeName;

        const numA = parseInt(nameA.match(/\d+$/)?.[0] || '0', 10);
        const numB = parseInt(nameB.match(/\d+$/)?.[0] || '0', 10);

        const baseA = nameA.replace(/\d+$/, '').trim();
        const baseB = nameB.replace(/\d+$/, '').trim();

        if (baseA === baseB) return numA - numB;
        return baseA.localeCompare(baseB);
      });
  }

  getDivisionNameById(id: number): string {
    const division = this.divisionList().find((div) => div.courtUnitTypeId === id);
    return division ? division.courtUnitTypeName : '';
  }

  public partyNameDisplayFn(item: IParty): string {
    return item ? item.partiesName : '';
  }

  private buildQueryParams(reportType: string): Record<string, any> {
    if (!this.formGrp) return {};

    const formData = this.formGrp.getRawValue();
    const getDate = (d: any) =>
      d ? encodeURIComponent(formatDate(d, 'dd-MMM-yyyy', 'en-US')) : '';

    const safeLabel = (obj: any) => (obj && typeof obj === 'object' ? obj.label ?? '' : '');
    const safeValue = (obj: any) => (obj && typeof obj === 'object' ? obj.value ?? '' : '');
    const safeId = (obj: any) => (obj && typeof obj === 'object' ? obj.id ?? '' : '');

    switch (reportType) {
      // ------------------ GROUP 1 ------------------
      case 'action-status-on-cases':
      case 'action-status-on-appeals':
      case 'case-duration':
      case 'case-history':
      case 'appeal-history':
      case 'current-phase-of-cases':
      case 'current-phase-of-appeals':
      case 'cases-pending-for-hearing':
      case 'appeals-pending-for-hearing':
      case 'list-of-cases-received':
      case 'list-of-appeals-received':
      case 'list-of-backlog-cases':
      case 'list-of-backlog-appeals':
      case 'list-of-users-and-roles':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
        };

      // ------------------ CASE APPEAL HEARING SCHEDULE ------------------
      case 'case-hearing-schedule':
      case 'appeal-hearing-schedule':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          caseCategory:
            formData.caseCategory?.value === 'FIRST INSTANCE'
              ? 'FI'
              : formData.caseCategory?.value === 'APPEAL'
                ? 'A'
                : '',
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
        };

      // ------------------ CASE HISTORY FOR COUNSEL ------------------
      case 'case-history-for-counsel':
      case 'appeal-history-for-counsel':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          partyName: safeLabel(formData.counselName),
          partyId: safeValue(formData.counselName),
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
        };

      // ------------------ CASE PROGRESS / MOTIONS ------------------
      case 'case-progress':
      case 'list-of-motions-filed-in-case':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          courtCaseNumber: formData.caseNumber ?? '',
        };

      // ------------------ CAUSE LIST ------------------

      case 'causelist':
        const reportType = safeLabel(formData.reportType);
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          caseCategory:
            formData.caseCategory?.value === 'FIRST INSTANCE'
              ? 'FI'
              : formData.caseCategory?.value === 'APPEAL'
                ? 'A'
                : '',
          reportType: safeLabel(formData.reportType),
          fromDate: getDate(formData.fromDate),

          tillDate:
            reportType === 'DATERANGE'
              ? getDate(formData.tillDate)
              : ''
        };

      // ------------------ DATE ALERT ------------------
      case 'date-alert-by-last-document-recorded':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          tillDate: getDate(formData.tillDate),
          caseCategory: safeValue(formData.caseCategory),
        };

      // ------------------ PENDING TASKS ------------------
      case 'pending-tasks-till-date':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          caseCategory: safeValue(formData.caseCategory),
          roleId: safeId(formData.roleName),
          tillDate: getDate(formData.tillDate),
        };

      // ------------------ DOCUMENTS / JUDGES ------------------
      case 'documents-received-for-case':
      case 'documents-received-for-appeal':
      case 'list-of-judges-that-handled-case':
      case 'list-of-justices-that-handled-appeal':
        return {
          courtCaseNumber: formData.caseNumber ?? formData.caseNumber?.caseNumber ?? '',
        };

      // ------------------ JUDGE PANEL ------------------
      case 'judge-panel-for-appeals':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          courtCaseNumber: formData.appealNumber?.caseNumber ?? '',
          appealSelectionFlag: 'Y',
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
        };

      // ------------------ CASES BY JUDGE ------------------
      case 'list-of-cases-by-judge':
      case 'list-of-cases-handled-by-judge':
      case 'list-of-appeals-handled-by-justice':
      case 'list-of-appeals-by-justice':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          partyName: safeLabel(formData.judge),
          partyId: safeValue(formData.judge),
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
        };

      // ------------------ CASE STATISTICS ------------------
      case 'case-statistics':
      case 'appeal-statistics':
        return {
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
          partyId: safeValue(formData.partyName),
          partyNameRep: safeLabel(formData.partyName),
          dateOfFiling: getDate(formData.filedOn),
          appealStatusName: safeValue(formData.caseStatus),
          casePhTypesName: safeValue(formData.casePhase),
          courtDivisionId: safeValue(formData.courtDivision),
          courtDivisionName: safeLabel(formData.courtDivision),
          caseTypesName: safeValue(formData.caseType),
          courtComboName: safeLabel(formData.courts),
          courtsComboId: safeValue(formData.courts),
        };

      // ------------------ PENDING CASE STATISTICS ------------------
      case 'pending-case-statistics-report':
        return {
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
          partyNameRep: safeValue(formData.partyName),
          partyId: safeId(formData.partyName),
          caseTypesName: safeLabel(formData.caseType),
          courtDivisionId: safeValue(formData.courtDivision),
          courtDivisionName: safeLabel(formData.courtDivision),
          courtComboId: safeId(formData.courts),
          courtComboName: safeValue(formData.courts),
        };

      // ------------------ STAFF ASSIGNMENT ------------------
      case 'staff-assignment-history':
        return {
          courtDivisionId: safeValue(formData.courtDivision),
          courtDivisionName: safeLabel(formData.courtDivision),
          staffRole: safeValue(formData.roleName),
          staffRoleName: safeLabel(formData.roleName),
          staffNameStr: safeValue(formData.staffName),
          staffId: safeValue(formData.staffName),
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
          childCourtsId: safeValue(formData.courts),
        };

      // ------------------ CASE STATUS ------------------
      case 'list-of-appeals-by-status':
        return {
          courtDivisionId: safeValue(formData.courtDivision),
          courtDivisionName: safeLabel(formData.courtDivision),
          appealStatusName: safeLabel(formData.caseStatus),
          appealStatusId: safeValue(formData.caseStatus),
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
        };

      // ------------------ AUDIT TRAIL ------------------
      case 'audit-trail':
        return {
          partyId: safeValue(formData.partyName1),
          fromDate: getDate(formData.fromDate),
          tillDate: getDate(formData.tillDate),
        };
      case 'appeal-summary-based-on-appeal-type':
      case 'appeals-by-lower-court':

        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          fromDate: getDate(formData.fromDate),
          toDate: getDate(formData.tillDate),
        };

      case 'appeals-by-party-name':
      case 'appeal-details-based-on-party-name':
        return {
          courtDivisionName: safeLabel(formData.courtDivision),
          courtDivisionId: safeValue(formData.courtDivision),
          partyName: safeLabel(formData.partyName2),
          partyId: safeValue(formData.partyName2),
        };

      case 'case-assignment-statistics':
        return {
          fromDate: getDate(formData.fromDate),
          toDate: getDate(formData.tillDate),
        };
      default:
        console.warn(`⚠️ Unhandled report type: ${reportType}`);
        return {};
    }
  }
  private getReportEndpoint(reportType: string): string {
    switch (reportType) {

      // ------------------ GROUP 1 ------------------
      case 'action-status-on-cases':
      case 'action-status-on-appeals':
        return 'actionStatusOnCases/report/view-report.htm';
      case 'case-duration':
        return 'caseDuration/report/view-report.htm';
      case 'case-history':
      case 'appeal-history':
        return 'caseHistory/report/view-report.htm';
      case 'current-phase-of-cases':
      case 'current-phase-of-appeals':
        return 'casePhase/report/view-report.htm';
      case 'cases-pending-for-hearing':
      case 'appeals-pending-for-hearing':
        return 'cases-heard/report/view-report.htm';
      case 'list-of-cases-received':
      case 'list-of-appeals-received':
        return 'cases-received/report/view-report.htm';
      case 'list-of-backlog-cases':
      case 'list-of-backlog-appeals':
        return 'backlog-appeals/report/view-report.htm';
      case 'list-of-users-and-roles':
        return 'user-dtls/report/view-report.htm';

      // ------------------ CASE APPEAL HEARING SCHEDULE ------------------
      case 'case-hearing-schedule':
      case 'appeal-hearing-schedule':
        return 'caseHearingSchedule/report/view-report.htm';

      // ------------------ CASE HISTORY FOR COUNSEL ------------------
      case 'case-history-for-counsel':
      case 'appeal-history-for-counsel':
        return 'counselList/report/view-report.htm';

      // ------------------ CASE PROGRESS / MOTIONS ------------------
      case 'case-progress':
        return 'caseProgress/report/view-report.htm';
      case 'list-of-motions-filed-in-case':
        return 'motions-filed-case/report/view-report.htm';

      // ------------------ CAUSE LIST ------------------
      case 'causelist':
        return 'cause-list/report/view-report.htm';

      // ------------------ DATE ALERT ------------------
      case 'date-alert-by-last-document-recorded':
        return 'dateAlertByLastDocumentRecorded/report/view-report.htm';

      // ------------------ PENDING TASKS ------------------
      case 'pending-tasks-till-date':
        return 'pendingTasksTillDate/report/view-report.htm';

      // ------------------ DOCUMENTS / JUDGES ------------------
      case 'documents-received-for-case':
      case 'documents-received-for-appeal':
        return 'documents-received/report/view-report.htm';
      case 'list-of-judges-that-handled-case':
      case 'list-of-justices-that-handled-appeal':
        return 'justices-handled-case/report/view-report.htm';

      // ------------------ JUDGE PANEL ------------------
      case 'judge-panel-for-appeals':
        return 'justice-panel-appeals/report/view-report.htm';

      // ------------------ CASES BY JUDGE ------------------
      case 'list-of-cases-by-judge':
      case 'list-of-appeals-by-justice':
        return 'cases-list/judge/report/view-report.htm';
      case 'list-of-cases-handled-by-judge':
      case 'list-of-appeals-handled-by-justice':
        return 'cases-handled/judge/report/view-report.htm';

      // ------------------ CASE STATISTICS ------------------
      case 'case-statistics':
      case 'appeal-statistics':
        return 'reports/common/view/statistics/report.htm';

      // ------------------ PENDING CASE STATISTICS ------------------
      case 'pending-case-statistics-report':
        return 'pending-case-statistics/report/view-report.htm';

      // ------------------ STAFF ASSIGNMENT ------------------
      case 'staff-assignment-history':
        return 'staff-assignment-history/report/view-report.htm';

      // ------------------ CASE STATUS ------------------
      case 'list-of-appeals-by-status':
        return 'appeals-by-status/report/view-report.htm';

      // ------------------ AUDIT TRAIL ------------------
      case 'audit-trail':
        return 'audit-trail/report/view-report.htm';

      // ------------------ Appeal Summary Based on Appeal Type ------------------
      case 'appeal-summary-based-on-appeal-type':
        return 'case-details-based-on-case-type/report/view-report.htm';

      case 'appeals-by-lower-court':
        return 'appeal-details-based-on-appeal-types/report/view-report.htm';

      case 'appeals-by-party-name':
        return 'case-details/report/view-report.htm';

      case 'appeal-details-based-on-party-name':
        return 'appeal-details/report/view-report.htm';

      case 'case-assignment-statistics':
        return 'assignment-statistics/report/view-report.htm';

      // ------------------ DEFAULT ------------------
      default:
        console.warn(`⚠️ Unhandled report type for endpoint: ${reportType}`);
        return 'default/report/view-report.htm';
    }
  }



  // 🔁 Filter options based on search text
  onSearchInput(fieldName: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const search = target.value.toLowerCase().trim();

    this.searchTextMap[fieldName] = search;

    const allOptions = this.selectOptionsMap[fieldName] || [];
    this.filteredOptionsMap[fieldName] = !search
      ? [...allOptions]
      : allOptions.filter(opt =>
        opt.label?.toLowerCase().includes(search)
      );
  }


  // ✅ Modify preloadSelectOptions to initialize filteredOptionsMap
  private preloadSelectOptions(): void {
    const selectFields = this.fields.filter(f => f.type === 'select');

    selectFields.forEach((f) => {
      const fieldName = f.name;

      // 🚫 DO NOT preload courts — they depend on division
      if (fieldName === 'courts') return;

      this.loadingMap[fieldName] = true;

      this.fetchSelectOptions(fieldName)
        .pipe(finalize(() => (this.loadingMap[fieldName] = false)))
        .subscribe({
          next: (options) => {
            this.selectOptionsMap[fieldName] = options;
            this.filteredOptionsMap[fieldName] = [...options];

            if (options.length === 1) {
              this.formGrp.get(fieldName)?.setValue(options[0]);
              this.onSelectChange(fieldName, { value: options[0] } as MatSelectChange);
            }
          },
          error: () => {
            this.selectOptionsMap[fieldName] = [];
            this.filteredOptionsMap[fieldName] = [];
          },
        });
    });
  }




  private loadCourtsForDivision(divisionId: number): void {
    this.loadingMap['courts'] = true;

    this.service.getCourtsByDivision({ courtUnitTypeId: divisionId })
      .pipe(finalize(() => (this.loadingMap['courts'] = false)))
      .subscribe({
        next: (res) => {
          const courts = (res.data || res.success || []).map((d: any) => ({
            label: d.courtUnitTypeName,
            value: d.courtUnitTypeId
          }));

          console.log('COURTS LOADED:', courts);

          // Save dropdown values
          this.selectOptionsMap['courts'] = courts;
          this.filteredOptionsMap['courts'] = [...courts];

          if (courts.length === 1) {
            this.formGrp.get('courts')?.setValue(courts[0]);
            this.onSelectChange('courts', { value: courts[0] } as MatSelectChange);
          }

          // Allow Angular to refresh dropdown
          this.formGrp.get('courts')?.enable();
          this.formGrp.get('courts')?.updateValueAndValidity();
        },
        error: (err) => {
          console.error('Failed to load courts:', err);
          this.selectOptionsMap['courts'] = [];
          this.filteredOptionsMap['courts'] = [];
        }
      });
  }

  private loadCounselForDivision(divisionId: number): void {
    this.loadingMap['counselName'] = true;

    this.service.getCounelName({ courtDivisionId: divisionId })
      .pipe(finalize(() => (this.loadingMap['counselName'] = false)))
      .subscribe({
        next: (res) => {
          const counselName = (res.data || res.success || []).map((d: any) => ({
            label: d.partiesName,
            value: d.partiesId
          }));

          console.log('COURTS LOADED:', counselName);

          // Save dropdown values
          this.selectOptionsMap['counselName'] = counselName;
          this.filteredOptionsMap['counselName'] = [...counselName];

          if (counselName.length === 1) {
            this.formGrp.get('counselName')?.setValue(counselName[0]);
            this.onSelectChange('counselName', { value: counselName[0] } as MatSelectChange);
          }

          // Allow Angular to refresh dropdown
          this.formGrp.get('counselName')?.enable();
          this.formGrp.get('counselName')?.updateValueAndValidity();
        },
        error: (err) => {
          console.error('Failed to load counsel:', err);
          this.selectOptionsMap['counselName'] = [];
          this.filteredOptionsMap['counselName'] = [];
        }
      });
  }


  onSelectChange(fieldName: string, event: MatSelectChange): void {
    const selectedOption = event.value;

    switch (fieldName) {

      case 'courtDivision':
        const divisionId = selectedOption?.value ?? null;
        const allRoles1 = this.selectOptionsMap['roleNameOriginal'] || [];

        // 1. Reset dependent fields
        this.resetDependentFields(['courts', 'roleName', 'staffName']);

        // 2. Check if the current form actually has a 'courts' field
        const hasCourtField = !!this.formGrp.get('courts');
        const hasCounselField = !!this.formGrp.get('counselName');

        // 3. Handle No Division Selected
        if (!divisionId) {
          if (hasCourtField) {
            this.formGrp.get('courts')?.disable();
            this.selectOptionsMap['courts'] = [];
            this.filteredOptionsMap['courts'] = [];
          }
          if (hasCounselField) {
            this.formGrp.get('counselName')?.disable();
            this.selectOptionsMap['counselName'] = [];
            this.filteredOptionsMap['counselName'] = [];
          }
          // Reset roles to original list
          this.selectOptionsMap['roleName'] = allRoles1;
          this.filteredOptionsMap['roleName'] = [...allRoles1];
          return;
        }
        if (hasCounselField) {
          // ONLY enable and fetch if the field exists in this report type
          this.formGrp.get('counselName')?.enable();
          this.loadCounselForDivision(divisionId);
        }
        // 4. Handle Division Selected
        if (hasCourtField) {
          // ONLY enable and fetch if the field exists in this report type
          this.formGrp.get('courts')?.enable();
          this.loadCourtsForDivision(divisionId);
        } else {
          // If there is no 'courts' field, ensure roles are available for selection immediately
          this.selectOptionsMap['roleName'] = allRoles1;
          this.filteredOptionsMap['roleName'] = [...allRoles1];
          if (allRoles1.length === 1) {
            this.formGrp.get('roleName')?.setValue(allRoles1[0]);
            this.onSelectChange('roleName', { value: allRoles1[0] } as MatSelectChange);
          }
        }
        break;
      case 'courts':
        const hasCourt = !!selectedOption?.value;
        const allRoles = this.selectOptionsMap['roleNameOriginal'] || [];

        const filteredRoles = hasCourt
          ? allRoles.filter(r => r.label === 'Judge' || r.label === 'Court Registrar')
          : allRoles;

        this.selectOptionsMap['roleName'] = filteredRoles;
        this.filteredOptionsMap['roleName'] = [...filteredRoles];

        this.formGrp.get('roleName')?.reset();
        this.formGrp.get('staffName')?.reset();

        if (filteredRoles.length === 1) {
          this.formGrp.get('roleName')?.setValue(filteredRoles[0]);
          this.onSelectChange('roleName', { value: filteredRoles[0] } as MatSelectChange);
        }
        break;
      // ------------------ ROLE NAME CHANGED ------------------
      case 'roleName':
        const roleId = selectedOption?.value ?? null;

        // Reset staffName dropdown always
        const staffCtrl = this.formGrp.get('staffName');
        if (staffCtrl) {
          staffCtrl.reset();
          staffCtrl.disable();  // Disable immediately
        }

        // If roleName is unselected → keep staff disabled
        if (!roleId) break;

        // If staffName field does not exist → nothing to do
        const hasStaffNameField = this.fields.some(f => f.name === 'staffName');
        if (!hasStaffNameField) break;

        // Enable staffName (we have a role now)
        staffCtrl?.enable();
        const divisionCtrl = this.formGrp.get('courtDivision')?.value;
        const courtCtrl = this.formGrp.get('courts')?.value;
        const roleCtrl = this.formGrp.get('roleName')?.value;

        const payload: RoleStaff = {
          divisionId: divisionCtrl?.value ?? '',
          cutId: courtCtrl?.value ?? '',
          roleName: roleCtrl.label,
          intRoleId: roleCtrl.value?.toString() ?? ''
        };

        // Load staff list from API
        this.loadingMap['staffName'] = true;

        this.service.getStaffByRole(payload)
          .pipe(finalize(() => (this.loadingMap['staffName'] = false)))
          .subscribe({
            next: (res) => {
              const list = (res.data || []).map((s: any) => ({
                label: s.prtName,   // adjust to your API
                value: s.prtId
              }));

              this.selectOptionsMap['staffName'] = list;
              this.filteredOptionsMap['staffName'] = [...list];

              if (list.length === 1) {
                this.formGrp.get('staffName')?.setValue(list[0]);
                this.onSelectChange('staffName', { value: list[0] } as MatSelectChange);
              }
            },
            error: () => {
              this.selectOptionsMap['staffName'] = [];
              this.filteredOptionsMap['staffName'] = [];
            }
          });

        break;
      case 'reportType':
        if (this.currentReportType === 'causelist') {
          this.handleDateValidation(selectedOption.value);
        }
        else {
          break;
        }
        break;


      default:
        break;
    }
  }

  private handleDateValidation(reportType: string) {
    const fromDate = this.formGrp.get('fromDate');
    const toDate = this.formGrp.get('tillDate');

    switch (reportType) {
      case 'DATERANGE':
        // Both are required
        this.setRequired(fromDate);
        this.setRequired(toDate);
        break;

      case 'DAILY':
        // Only From Date is required; Clear To Date
        this.setRequired(fromDate);
        this.clearValidation(toDate);
        break;

      default:
        // For 'causelist' or any other type: Clear both
        this.clearValidation(fromDate);
        this.clearValidation(toDate);
        break;
    }
  }

  // Helper: Make field required
  private setRequired(control: any) {
    if (control) {
      control.setValidators([Validators.required]);
      control.updateValueAndValidity();
    }
  }

  // Helper: Clear validation and value
  private clearValidation(control: any) {
    if (control) {
      control.clearValidators();
      control.setValue(null); // Optional: wipe data so it doesn't send to MySQL
      control.setErrors(null);
      control.updateValueAndValidity();
    }
  }



  shouldShowField(f: any): boolean {
    // 1. Force hide caseCategory for Supreme Court
    if (this.isSupremeCourtFlg() && f.name === 'caseCategory') {
      const control = this.formGrp.get('caseCategory');
      if (control) {
        control.clearValidators();     // Removes 'required' and other validators
        control.updateValueAndValidity(); // Refreshes the control state
      }
      return false;
    }

    // 2. Existing showIf logic
    if (!f.showIf) return true;

    const control = this.formGrp.get(f.showIf.field);
    if (!control) return false;

    const selected = control.value?.value || control.value;
    return selected === f.showIf.value;
  }


  // 🔁 Centralized fetch function
  private fetchSelectOptions(fieldName: string): Observable<ISelectOption[]> {
    switch (fieldName) {
      // case 'courtDivision':
      //   return this.service.getDivisions().pipe(
      //     map(res => {
      //       const list = res.data || res.success || [];
      //       return list.map((d: any) => ({
      //         label: d.courtUnitTypeName,
      //         value: d.courtUnitTypeId
      //       }));
      //     })
      //   );

      case 'caseCategory':
        return of(['FIRST INSTANCE', 'APPEAL'].map(c => ({ label: c, value: c })));

      case 'roleName':
        return this.service.getRoles().pipe(
          map(res => {
            const list = (res.data || res.success || []).map((r: any) => ({
              label: r.roleName,
              value: r.intRoleId,
              rcaCode: r.rcaCode   // <-- needed for filtering
            }));

            // Store a full copy to use for court filtering later
            this.selectOptionsMap['roleName'] = list;
            this.selectOptionsMap['roleNameOriginal'] = [...list];

            return list;
          })
        );

      case 'reportType':
        return of(['MONTHLY', 'WEEKLY', 'DAILY', 'DATERANGE'].map(r => ({ label: r, value: r })));

      case 'caseStatus':
        return this.service.getCaseStatus().pipe(
          map(res => (res.data || res.success || []).map((c: any) => ({
            label: c.name,
            value: c.caseStatusId
          })))
        );
      case 'caseTypes':
      case 'caseType':
        return this.service.getCaseTypes().pipe(
          map(res => (res.data || res.success || []).map((c: any) => ({
            label: c.name,
            value: c.id
          })))
        );
      case 'casePhase':
        return this.service.getCasePhases().pipe(
          map(res => (res.data || res.success || []).map((t: any) => ({
            label: t.casePhTypesName,
            value: t.casePhTypesId
          })))
        );

      case 'staffName':
        return of(
          (this.selectOptionsMap['staffName'] || []).map((s: any) => ({
            label: s.label ?? s.prtName,
            value: s.value ?? s.prtId
          }))
        );

      case 'judge':
        return this.service.getJudges().pipe(
          map(res => (res.data || res.success || []).map((j: any) => ({
            label: j.prtName,
            value: j.prtId
          })))
        );
      case 'partyName1':
        return this.service.getStaffName().pipe(
          map(res => (res.data || res.success || []).map((j: any) => ({
            label: j.partiesName,
            value: j.partiesId
          })))
        );
      case 'partyName2':
        return this.service.getStaffNameSCN().pipe(
          map(res => (res.data || res.success || []).map((j: any) => ({
            label: j.partiesName,
            value: j.partiesId
          })))
        );
      case 'courts':
        return of(
          (this.selectOptionsMap['courts'] || []).map((c: any) => ({
            label: c.label,
            value: c.value
          })));
      case 'counselName':
        return of(
          (this.selectOptionsMap['counselName'] || []).map((c: any) => ({
            label: c.label,
            value: c.value
          })));


      default:
        return of([]);
    }
  }
  // Inside your component class

  getMinDate(field: any): Date | null {
    // 1. Check if this field represents a "Till Date"
    // You can check by specific name OR by a custom property in your config
    if (field.name === 'tillDate' || field.isEndDate) {

      // 2. Identify the name of the corresponding "From Date" field
      // Example: assuming the start date field is named 'fromDate'
      const fromDateFieldName = 'fromDate';
      // OR if dynamic: const fromDateFieldName = field.linkedStartDateField;

      // 3. Get the control and its value from the FormGroup
      const fromDateControl = this.formGrp.get(fromDateFieldName);

      // 4. If the control exists and has a value, return it as the min date
      if (fromDateControl && fromDateControl.value) {
        return new Date(fromDateControl.value);
      }
    }

    // 5. Default: No limit (returns null)
    return null;
  }
  private resetDependentFields(fields: string[]): void {
    fields.forEach(f => {
      const ctrl = this.formGrp.get(f);
      if (!ctrl) return;

      ctrl.reset();

      if (this.selectOptionsMap[f]) {
        this.filteredOptionsMap[f] = [...this.selectOptionsMap[f]];
      }
    });
  }




  // 🔁 Helper functions
  getSelectOptions(name: string): any[] {
    return this.selectOptionsMap[name] || [];
  }

  getOptionLabel(opt: any): string {
    return typeof opt === 'string'
      ? opt
      : opt.label || opt.name || opt.courtUnitTypeName || JSON.stringify(opt);
  }

}
